// © Microsoft Corporation. All rights reserved.
import { CustomEventPayloadStage, CustomWidgetErrorEvent, dispatchCustomEvent } from "./ErrorDispatcher";
import { PeregrineFlightPrefix, Prong2FlightPrefix, coreBundleClassName, headTagDataInfoAttribute, headTagtClientSettingAttribute, widgetGroupClassName, widgetSettingsClassName } from "@msnews/experiences-constants";
import { clearNetworkCachedResponse, getPcsResponse } from "./GetPcsResponse";
import { getPeregrineFlights, getProng2FlightList, shouldOnlyCheckProng2Flights } from "@msnews/experimentation-helpers";
import { getDataWidgetAttributes } from "./GetDataWidgetAttributes";
import { getLocalStorage } from "@msnews/core";
import { setLoggingMetadata } from "./ErrorLogging";
import { isMarketSelectorEnabled, shouldRenderWidgets } from "./Utilities";
/**
 * Function called by the Widgets host page.
 * This checks if widget's artifacts are changed so host page can refresh entire page.
 */
export async function widgetsChanged() {
    // Return if host page browser isn't supported.
    if (!shouldRenderWidgets()) {
        return {
            isSuccess: false,
            description: "shouldRenderWidgets function returned false",
            widgetsChangedInfoVersion: 2
        };
    }
    const safeLocalStorage = getLocalStorage();
    // Record if WidgetChanged is called. For Prong2, we'll use this to check if contentrefresh succeeds.
    safeLocalStorage.setItem("WidgetChangedCalled", "1");
    let dataWidgetAttributes;
    try {
        // Check if host page has data-info and client settings in the DOM.
        const peregrineWidgetGroupDiv = document.getElementsByClassName(widgetGroupClassName)[0];
        const widgetSettingsDiv = peregrineWidgetGroupDiv && peregrineWidgetGroupDiv.getElementsByClassName(widgetSettingsClassName)[0];
        const dataInfo = widgetSettingsDiv && widgetSettingsDiv.getAttribute(headTagDataInfoAttribute);
        const clientSettings = widgetSettingsDiv && widgetSettingsDiv.getAttribute(headTagtClientSettingAttribute);
        // Check if host page has script tags already present for Peregrine core bundles
        const coreBundleScripts = peregrineWidgetGroupDiv && peregrineWidgetGroupDiv.getElementsByClassName(coreBundleClassName);
        // Proceed only if host page has specific data attributes and core bundle scripts in the DOM.
        if (!dataInfo
            || !clientSettings
            || !coreBundleScripts
            || coreBundleScripts.length < 1) {
            safeLocalStorage.setItem("widgetsChanged", `${Date.now()}-empty`);
            return {
                isSuccess: true,
                anyChange: true,
                description: "Host page doesn't have either data-info and data-client-settings in the DOM or it doesn't have core bundle scripts already on the page",
                changeReason: {
                    flightsChanged: true,
                    buildVersionChanged: true,
                    bundleHashChanged: true,
                    marketChanged: false
                },
                widgetsChangedInfoVersion: 2
            };
        }
        // Get widget attributes from query params and/or inline specified in the div.
        dataWidgetAttributes = getDataWidgetAttributes();
        if (dataWidgetAttributes) {
            dataWidgetAttributes = { ...dataWidgetAttributes, isCallFromWidgetsChanged: true };
        }
        // Set app error meta data attributes
        setLoggingMetadata(dataWidgetAttributes);
        // Clear memoize cache.
        clearNetworkCachedResponse();
        // Call PCS API with data widget attributes
        const responseData = await getPcsResponse(dataWidgetAttributes);
        if (!responseData
            || !responseData.pcsData) {
            dispatchCustomEvent(CustomWidgetErrorEvent, {
                stage: CustomEventPayloadStage.CHANGE,
                message: "Call to PCS API did not return data when widget changed!",
                extra: {
                    attributes: dataWidgetAttributes
                }
            });
            return {
                isSuccess: false,
                description: "PCS response is invalid. Check app errors.",
                widgetsChangedInfoVersion: 2
            };
        }
        const { appType } = dataWidgetAttributes || {};
        const currentFlightInfo = getProng2FlightList(responseData.pcsData.info || "");
        const lastFlightInfo = getProng2FlightList(dataInfo || "");
        // Get the flight prefix we need to check
        const checkProng2Flights = shouldOnlyCheckProng2Flights(appType);
        const flightPrefix = checkProng2Flights ? Prong2FlightPrefix : PeregrineFlightPrefix;
        // Get current peregrine/prong2 flights and last peregrine/prong2 flights
        const currentFlights = getPeregrineFlights(currentFlightInfo, flightPrefix).join(",");
        const lastFlights = getPeregrineFlights(lastFlightInfo, flightPrefix).join(",");
        // Get last hashes from DOM
        const lastClientSettings = JSON.parse(clientSettings);
        const { bundleInfo: lastBundleInfo } = lastClientSettings || {};
        const { v: lastbuildVersion } = lastBundleInfo || {};
        // Get current hashes from pcs response
        const { bundleInfo: currentBundleInfo } = responseData.pcsData.clientSettings || {};
        const { v: currentBuildVersion } = currentBundleInfo || {};
        // Check if CM changed for prong2
        // The content maket should match with the cm value and the detection type should be cm
        const marketSelectorEnabled = isMarketSelectorEnabled(appType, currentFlightInfo);
        let cmChanged = false;
        if (marketSelectorEnabled) {
            safeLocalStorage.setItem("Prong2MarketSelectorEnabled", "1");
            const currentLanguage = responseData.pcsData.clientSettings?.locale?.content?.language || "";
            const currentMarket = responseData.pcsData.clientSettings?.locale?.content?.market || "";
            const lastLanguage = lastClientSettings?.locale?.content?.language || "";
            const lastMarket = lastClientSettings?.locale?.content?.market || "";
            const currentCM = `${currentLanguage}-${currentMarket}`;
            const lastCM = `${lastLanguage}-${lastMarket}`;
            if (currentCM !== lastCM) {
                cmChanged = true;
            }
        }
        // Check if flights, build version and market are same
        if (lastFlights === currentFlights
            && lastbuildVersion === currentBuildVersion
            && !cmChanged) {
            return {
                isSuccess: true,
                anyChange: false,
                description: "No change detected in newer PCS response versus attributes in the DOM",
                changeReason: {
                    flightsChanged: false,
                    buildVersionChanged: false,
                    bundleHashChanged: false,
                    marketChanged: false
                },
                widgetsChangedInfoVersion: 2
            };
        }
        const flightsChanged = lastFlights !== currentFlights;
        const buildVersionChanged = lastbuildVersion !== currentBuildVersion;
        safeLocalStorage.setItem("widgetsChanged", `${Date.now()}-${flightsChanged}-${buildVersionChanged}${isMarketSelectorEnabled ? `-${cmChanged}` : ""}`);
        // Mark anyChange to be false with appropriate change reason
        return {
            isSuccess: true,
            anyChange: true,
            description: "Refer object changeReason for details",
            changeReason: {
                flightsChanged: lastFlights !== currentFlights,
                buildVersionChanged: lastbuildVersion !== currentBuildVersion,
                bundleHashChanged: lastbuildVersion !== currentBuildVersion,
                marketChanged: cmChanged
            },
            widgetsChangedInfoVersion: 2
        };
    }
    catch (error) {
        dispatchCustomEvent(CustomWidgetErrorEvent, {
            stage: CustomEventPayloadStage.CHANGE,
            message: `Error occured when widget changed: ${error}`,
            extra: {
                attributes: dataWidgetAttributes
            }
        });
        return {
            isSuccess: false,
            description: `Error in catch block: ${error}`,
            widgetsChangedInfoVersion: 2
        };
    }
}
//# sourceMappingURL=WidgetsChanged.js.map